import { BannerModel } from '@/models/Creative/Banner'
import Compressor from 'compressorjs'
import TinyPngApi from '@/models/Creative/TinyPngApi'
import { CannotCompressImageError, OverSizeImageError } from './Error'

/**
 * 警告圧縮率。
 * @type {number}
 */
export const MAX_COMPRESSION_RATE = 0.7

/**
 * @param file
 * @returns fileObject || Exception
 */
export const optimizeFile = async file => {
  const optimizeStrategy = getOptimizeStrategyFromFileType(file.type)
  if (file.size < BannerModel.maxSize || !optimizeStrategy) {
    return { optimizedFile: file, isCheckOverCompressionRate: false }
  }
  try {
    const optimizedFile = await optimizeStrategy.optimize(file)
    const isCheckOverCompressionRate = optimizeStrategy.checkOverCompressionRate(
      file,
      optimizedFile.size,
    )
    return { optimizedFile, isCheckOverCompressionRate }
  } catch (e) {
    return e
  }
}

// fileTypeを受け取り圧縮方法を返す関数
const getOptimizeStrategyFromFileType = fileType => {
  if (fileType === 'image/jpeg') return JpegOptimizeStrategy
  if (fileType === 'image/png') return PingOptimizeStrategy
  return false
}

// Jpegの圧縮方法
class JpegOptimizeStrategy {
  static async optimize(file) {
    // BannerModel.maxSize以下になるまで画像圧縮を行い最適化する
    for (let qualityLevel = 10; qualityLevel >= 0; --qualityLevel) {
      const compressedFile = await compressJPEG(file, qualityLevel / 10)
      if (compressedFile.size < BannerModel.maxSize) {
        return compressedFile
      }
      if (qualityLevel === 0) {
        return compressedFile
      }
    }
  }

  static checkOverCompressionRate(originalFile, optimizeFileSize) {
    return 1 - optimizeFileSize / originalFile.size > MAX_COMPRESSION_RATE
  }
}

// Pingの圧縮方法
class PingOptimizeStrategy {
  static async optimize(file) {
    if (file.size > BannerModel.maxPingSize) {
      throw new OverSizeImageError(
        `自動圧縮できるPNG画像は150KBまでです。${BannerModel.maxPingSize /
          1024}KB以下の画像を入稿してください。`,
      )
    }
    const response = await TinyPngApi.postCompressor(file)
    const fetchBlob = await fetch(response.url)
    const blob = await fetchBlob.blob()
    const compressedFile = new File([blob], file.name)
    if (compressedFile.size > BannerModel.maxSize) {
      throw new CannotCompressImageError(
        `${BannerModel.maxSize /
          1024}KB以下に圧縮できませんでした。再度小さいサイズの画像でお試しください。`,
      )
    }
    return compressedFile
  }

  /* eslint-disable no-unused-vars */
  static checkOverCompressionRate(originalFile, optimizeFileSize) {
    return false
  }
}

export const compressJPEG = async (file, quality) => {
  return new Promise(resolve => {
    new Compressor(file, {
      quality,
      success: resolve,
    })
  })
}

export const loadImage = fileOrUrl => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject()
    if (typeof fileOrUrl === 'string') {
      const url = fileOrUrl
      image.src = url
    } else {
      image.src = URL.createObjectURL(fileOrUrl)
    }
  })
}

export const isOverSize = file => {
  // jpegの場合はファイルサイズをチェックしない
  if (file.type === 'image/jpeg') return false
  return file.size > BannerModel.maxSize
}

export const fileToBase64 = file => {
  if (!file) return Promise.resolve(undefined)
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener(
      'load',
      () => {
        // data:image/png;base64, を省く
        resolve(reader.result.split(',')[1])
      },
      false,
    )
    reader.addEventListener('error', reject)
    reader.readAsDataURL(file)
  })
}

const round = (number, precision) => {
  var shift = function(number, precision, reverseShift) {
    if (reverseShift) {
      precision = -precision
    }
    var numArray = ('' + number).split('e')
    return +(
      numArray[0] +
      'e' +
      (numArray[1] ? +numArray[1] + precision : precision)
    )
  }
  return shift(Math.round(shift(number, precision, false)), precision, true)
}

export const bytesToSize = bytes => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  if (bytes == 0) return '0 Byte'
  const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)))
  return round(bytes / Math.pow(1024, i), 1) + ' ' + sizes[i]
}

export const validateUrl = url => /^https?:\/\/[^.]+\.\w/.test(url)

// 機種依存文字
export const checkEnviromentDependentString = string => {
  const enviromentDependentString =
    '[①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ㍉㌔㌢㍍㌘㌧㌃㌶㍑㍗㌍㌦㌣㌫㍊㌻㎜㎝㎞㎎㎏㏄㎡㍻〝〟№㏍℡㊤㊥㊦㊧㊨㈱㈲㈹㍾㍽㍼]'
  const ret = string.match(enviromentDependentString)
  if (ret) return false
  return true
}
